"use client";

import { useCallback, useEffect, useRef, type CSSProperties } from "react";
import Image from "next/image";
import Link from "@/components/ui/CcLink";
import {
  ArrowRight,
  Bot,
  ChevronRight,
  LayoutTemplate,
  Megaphone,
  Palette,
  Search,
  Target,
  type LucideIcon,
} from "lucide-react";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import {
  type CcSimplifyOperationsPillar,
  type CcSimplifyOperationsPillarIcon,
  type CcSimplifyOperationsPillarItem,
} from "@/components/home/simplify-operations/simplify-operations-data";
import { useSiteLocale } from "@/components/layout/CcLocaleContext";
import { localizeInternalHref } from "@/lib/i18n/paths";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { heroBandCardFloatProgressWithPageTopGuard } from "@/lib/simplify-ops-hero-band-float";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";
import { useCcScrollEffect } from "@/lib/use-cc-scroll-effect";
import { useCcSectionReveal } from "@/lib/use-cc-section-reveal";

const CC_SIMPLIFY_OPS_MD_BREAKPOINT = 810;

const PILLAR_ICONS: Record<CcSimplifyOperationsPillarIcon, LucideIcon> = {
  layout: LayoutTemplate,
  search: Search,
  bot: Bot,
  megaphone: Megaphone,
  target: Target,
  palette: Palette,
};

function pillarItemKey(item: CcSimplifyOperationsPillarItem, index: number) {
  return item.href ?? `${item.label}-${index}`;
}

function SimplifyOpsBulletList({
  items,
  variant,
}: {
  items: readonly CcSimplifyOperationsPillarItem[];
  variant: "default" | "bento";
}) {
  const locale = useSiteLocale();
  const listClassName =
    variant === "bento"
      ? "cc-simplify-ops-card__items cc-simplify-ops-card__items--bento"
      : "cc-simplify-ops-card__items";

  return (
    <ul className={listClassName}>
      {items.map((item, index) => (
        <li key={pillarItemKey(item, index)}>
          {item.href ? (
            <Link
              href={localizeInternalHref(item.href, locale)}
              className="cc-simplify-ops-card__item-link"
            >
              <span className="cc-simplify-ops-card__item-label">{item.label}</span>
              {variant === "bento" ? (
                <ChevronRight
                  className="cc-simplify-ops-card__item-chevron"
                  size={14}
                  strokeWidth={2}
                  aria-hidden="true"
                />
              ) : null}
            </Link>
          ) : (
            item.label
          )}
        </li>
      ))}
    </ul>
  );
}

function cardMeetsAmountZero(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  return (
    rect.bottom > 0 &&
    rect.top < window.innerHeight &&
    rect.right > 0 &&
    rect.left < window.innerWidth
  );
}

type CcSimplifyOperationsProps = {
  showHeading?: boolean;
  placement?: "default" | "services";
  /** Dark hero-band surface (services overview sticky layout). */
  heroBand?: boolean;
  eyebrow?: string;
  title?: string;
  titleHtml?: string;
  lead?: string;
  ctaLabel?: string;
  ctaHref?: string;
  /** Opens book-call popup instead of navigating (matches nav CTA). */
  bookCallPopup?: boolean;
  /** When set, replaces default home pillars. */
  cards?: readonly CcSimplifyOperationsPillar[];
};

export function CcSimplifyOperations({
  showHeading = true,
  placement = "default",
  heroBand = false,
  eyebrow,
  title,
  titleHtml,
  lead,
  ctaLabel,
  ctaHref,
  bookCallPopup = false,
  cards,
}: CcSimplifyOperationsProps) {
  const locale = useSiteLocale();
  const prefersReducedMotion = useCcPrefersReducedMotion();
  const isServices = placement === "services";
  const useHeroBand = heroBand && !isServices;
  const { ref: sectionRef, withRevealClassName } = useCcSectionReveal({
    staggerChildren: true,
  });
  const cardsWrapRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const removeTransitionListenersRef = useRef<Array<() => void>>([]);

  const resolvedEyebrow = eyebrow ?? "";
  const resolvedTitle = title ?? "";
  const resolvedLead = lead ?? "";
  const showSectionHeading = isServices ? true : showHeading;
  const resolvedCards = cards ?? [];
  const showPrimaryCta = Boolean(
    ctaLabel && (ctaHref || bookCallPopup),
  );

  const primaryCta = showPrimaryCta ? (
    <CcButton
      className="cc-simplify-ops__cta"
      href={bookCallPopup ? undefined : ctaHref}
      bookCallPopup={bookCallPopup}
      variant="primary"
      expand
      expandCompact
      label={ctaLabel!}
    />
  ) : null;

  const scheduleWillChangeCleanup = useCallback((el: HTMLElement) => {
    const computed = getComputedStyle(el);
    if (!computed.willChange.includes("transform")) {
      return;
    }

    const onEnd = (event: TransitionEvent) => {
      if (event.target !== el) {
        return;
      }
      if (
        event.propertyName !== "transform" &&
        event.propertyName !== "opacity"
      ) {
        return;
      }
      el.style.willChange = "auto";
      el.removeEventListener("transitionend", onEnd);
    };

    el.addEventListener("transitionend", onEnd);
    removeTransitionListenersRef.current.push(() => {
      el.removeEventListener("transitionend", onEnd);
    });
  }, []);

  const latchCard = useCallback(
    (el: HTMLElement) => {
      if (el.classList.contains("is-inview")) {
        return;
      }
      el.classList.add("is-inview");
      observerRef.current?.unobserve(el);
      scheduleWillChangeCleanup(el);
    },
    [scheduleWillChangeCleanup],
  );

  const syncHeroBandCardMotion = useCallback(
    (card: HTMLElement) => {
      const motionEnabled =
        !prefersReducedMotion &&
        window.innerWidth >= CC_SIMPLIFY_OPS_MD_BREAKPOINT;
      const previousRaw = card.style.getPropertyValue("--cc-simplify-ops-float");
      const previousProgress = previousRaw
        ? Number.parseFloat(previousRaw)
        : undefined;
      const progress = motionEnabled
        ? heroBandCardFloatProgressWithPageTopGuard(card, previousProgress)
        : 1;
      card.style.setProperty("--cc-simplify-ops-float", String(progress));
    },
    [prefersReducedMotion],
  );

  const syncVisibleCards = useCallback(() => {
    const root = cardsWrapRef.current;
    if (!root) {
      return;
    }

    root.querySelectorAll<HTMLElement>(".cc-simplify-ops-card").forEach((card) => {
      if (useHeroBand) {
        syncHeroBandCardMotion(card);
        return;
      }

      const inView = cardMeetsAmountZero(card);
      if (inView) {
        latchCard(card);
      }
    });
  }, [latchCard, syncHeroBandCardMotion, useHeroBand]);

  useCcScrollEffect(syncVisibleCards);

  useEffect(() => {
    if (useHeroBand) {
      syncVisibleCards();
    }
  }, [prefersReducedMotion, syncVisibleCards, useHeroBand]);

  useEffect(() => {
    const root = cardsWrapRef.current;
    if (!root) {
      return;
    }

    const cards = root.querySelectorAll<HTMLElement>(".cc-simplify-ops-card");
    if (!cards.length) {
      return;
    }

    if (useHeroBand) {
      syncVisibleCards();
      return () => {
        cards.forEach((card) => {
          card.style.removeProperty("--cc-simplify-ops-float");
        });
      };
    }

    if (typeof IntersectionObserver === "undefined") {
      cards.forEach((card) => latchCard(card));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            latchCard(entry.target as HTMLElement);
          }
        }
      },
      { threshold: 0 },
    );

    observerRef.current = observer;
    cards.forEach((card) => observer.observe(card));
    syncVisibleCards();

    return () => {
      observerRef.current = null;
      observer.disconnect();
      removeTransitionListenersRef.current.forEach((remove) => remove());
      removeTransitionListenersRef.current = [];
    };
  }, [latchCard, syncVisibleCards, useHeroBand]);

  const sectionClassName = withRevealClassName(
    [
      "cc-section",
      "cc-simplify-ops",
      isServices ? "cc-simplify-ops--services" : "",
      useHeroBand ? "cc-simplify-ops--hero-band" : "",
      !isServices && !showHeading ? "cc-simplify-ops--cards-only" : "",
    ]
      .filter(Boolean)
      .join(" "),
  );

  const cardsClassName = isServices
    ? "cc-simplify-ops__bento"
    : "cc-simplify-ops__cards";

  return (
    <section
      ref={sectionRef}
      className={sectionClassName}
      id="simplify-operations"
      data-cc-nav={isServices || useHeroBand ? "light" : undefined}
      {...devSectionAttr("simplify-operations")}
    >
      {isServices ? (
        <div className="cc-simplify-ops__services-shell">
          {showSectionHeading ? (
            <header className="cc-simplify-ops__services-header">
              <CcSanjayaEyebrow>{resolvedEyebrow}</CcSanjayaEyebrow>
              {titleHtml ? (
                <h2
                  className="cc-simplify-ops__services-title"
                  dangerouslySetInnerHTML={{ __html: titleHtml }}
                />
              ) : (
                <h2 className="cc-simplify-ops__services-title">{resolvedTitle}</h2>
              )}
              <div className="cc-simplify-ops__services-lead-block">
                <p className="cc-simplify-ops__services-lead">{resolvedLead}</p>
                {primaryCta}
              </div>
            </header>
          ) : null}

          <div ref={cardsWrapRef} className={cardsClassName}>
            {resolvedCards.map((card, index) => {
              const Icon = PILLAR_ICONS[card.icon];
              return (
                <article
                  key={card.id}
                  className={[
                    "cc-simplify-ops-card",
                    "cc-simplify-ops-card--bento",
                    `cc-simplify-ops-card--${card.id}`,
                    `cc-simplify-ops-card--accent-${card.accent}`,
                  ].join(" ")}
                  style={{ "--cc-simplify-ops-stagger": index } as CSSProperties}
                >
                  <div className="cc-simplify-ops-card__bento-visual" aria-hidden="true">
                    <Image
                      src={card.imageSrc}
                      alt=""
                      fill
                      sizes="(min-width: 1200px) 45vw, (min-width: 810px) 50vw, 100vw"
                      className="cc-simplify-ops-card__image"
                    />
                    <div className="cc-simplify-ops-card__bento-scrim" />
                    <div className="cc-simplify-ops-card__bento-grid" />
                  </div>

                  <div className="cc-simplify-ops-card__bento-body">
                    <div className="cc-simplify-ops-card__bento-top">
                      <span className="cc-simplify-ops-card__index">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <span className="cc-simplify-ops-card__icon-badge">
                        <Icon size={22} strokeWidth={1.75} aria-hidden="true" />
                      </span>
                    </div>

                    <div className="cc-simplify-ops-card__intro">
                      {card.href ? (
                        <Link
                          href={localizeInternalHref(card.href, locale)}
                          className="cc-simplify-ops-card__title-link"
                        >
                          <h3 className="cc-simplify-ops-card__title">{card.title}</h3>
                        </Link>
                      ) : (
                        <h3 className="cc-simplify-ops-card__title">{card.title}</h3>
                      )}
                      <p className="cc-simplify-ops-card__description">
                        {card.description}
                      </p>
                    </div>

                    <SimplifyOpsBulletList items={card.items} variant="bento" />

                    {card.href && card.ctaLabel ? (
                      <Link href={localizeInternalHref(card.href, locale)} className="cc-simplify-ops-card__cta cc-simplify-ops-card__cta--bento">
                        <span>{card.ctaLabel}</span>
                        <span className="cc-simplify-ops-card__cta-arrow" aria-hidden="true">
                          <ArrowRight size={16} strokeWidth={2} />
                        </span>
                      </Link>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="cc-container cc-simplify-ops__container">
          {showSectionHeading ? (
            <header className="cc-simplify-ops__heading">
              <div className="cc-simplify-ops__title-block">
                <CcSanjayaEyebrow>{resolvedEyebrow}</CcSanjayaEyebrow>
                {titleHtml ? (
                  <h2
                    className="cc-simplify-ops__title"
                    dangerouslySetInnerHTML={{ __html: titleHtml }}
                  />
                ) : (
                  <h2 className="cc-simplify-ops__title">{resolvedTitle}</h2>
                )}
              </div>
              <div className="cc-simplify-ops__lead-block">
                <p className="cc-simplify-ops__description">{resolvedLead}</p>
                {primaryCta}
              </div>
            </header>
          ) : null}

          <div ref={cardsWrapRef} className={cardsClassName}>
            {resolvedCards.map((card, index) => (
              <article
                key={card.id}
                className="cc-simplify-ops-card"
                style={
                  useHeroBand
                    ? ({ "--cc-simplify-ops-stagger": index } as CSSProperties)
                    : undefined
                }
              >
                <div className="cc-simplify-ops-card__text">
                  <div className="cc-simplify-ops-card__intro">
                    <h3 className="cc-simplify-ops-card__title">{card.title}</h3>
                    <p className="cc-simplify-ops-card__description">
                      {card.description}
                    </p>
                  </div>
                  <div className="cc-simplify-ops-card__footer">
                    <SimplifyOpsBulletList items={card.items} variant="default" />
                    {card.href && card.ctaLabel ? (
                      <Link href={localizeInternalHref(card.href, locale)} className="cc-simplify-ops-card__cta">
                        {card.ctaLabel}
                        <span className="cc-simplify-ops-card__cta-arrow" aria-hidden="true">
                          <ArrowRight size={16} strokeWidth={2} />
                        </span>
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="cc-simplify-ops-card__visual" aria-hidden="true">
                  <Image
                    src={card.imageSrc}
                    alt=""
                    fill
                    sizes="(min-width: 810px) max((min(100vw - 40px, 1240px) * 0.6 - 40px) / 2, 1px), calc(100vw - 40px)"
                    className="cc-simplify-ops-card__image"
                  />
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
